import type {
  EntityDecision,
  EntityDecisions,
  PreviewStatement,
  ReconcileItem,
} from '@/lib/api';

/**
 * The validation-step MODEL: statements grouped by the entity they describe,
 * reconcile decisions looked up by any IRI a statement may show, and the
 * per-column rollup — pure data + functions shared by every surface that
 * renders the validation contract (the T1 wizard today; the T3 results-first
 * review rides the same functions).
 */

/** One thing's facts — the unit the validation list groups by. */
export interface EntityGroup {
  subject: string;
  /** The thing's name and class, where the plan or the store gives them. */
  label: string | null;
  klass: string | null;
  /** How many rows name the thing. */
  rows: number;
  statements: PreviewStatement[];
}

/** Group the preview window by subject, preserving first-appearance order.
 *  Repeated key rows fold into one group; non-key entities that carry
 *  statements (foreign relationships) get groups of their own. */
export function groupStatements(statements: PreviewStatement[]): EntityGroup[] {
  const bySubject = new Map<string, EntityGroup>();
  for (const s of statements) {
    let g = bySubject.get(s.subject);
    if (!g) {
      g = {
        subject: s.subject,
        label: s.subject_label ?? null,
        klass: s.subject_class ?? null,
        rows: s.subject_rows,
        statements: [],
      };
      bySubject.set(s.subject, g);
    }
    g.statements.push(s);
  }
  // What a thing is and is called first, then its facts by property and value,
  // so the same thing reads the same way on every page.
  const rank = (s: PreviewStatement) => (s.predicate === ':type' ? 0 : s.predicate === ':label' ? 1 : 2);
  for (const g of bySubject.values()) {
    g.statements.sort(
      (a, b) =>
        rank(a) - rank(b) ||
        a.predicate.localeCompare(b.predicate) ||
        (a.object_label ?? a.object).localeCompare(b.object_label ?? b.object, undefined, { numeric: true }),
    );
  }
  return Array.from(bySubject.values());
}

/** Reconcile items looked up by ANY IRI a statement can show for the entity:
 *  the minted IRI, and — once a link decision applies — the linked target.
 *  Preview statements are generated AFTER decisions, so a linked entity's
 *  statements carry the existing URI, not the minted one. */
export function buildItemIndex(
  items: ReconcileItem[],
  decisions: EntityDecisions,
): Map<string, ReconcileItem> {
  const index = new Map<string, ReconcileItem>();
  for (const item of items) {
    index.set(item.minted_iri, item);
    const d = decisions[item.minted_iri];
    if (d?.action === 'link') index.set(d.uri, item);
  }
  return index;
}

/** The decision in force for a reconcile item (absent = the default: mint). */
export function decisionFor(decisions: EntityDecisions, item: ReconcileItem): EntityDecision {
  return decisions[item.minted_iri] ?? { action: 'create_new' };
}

/** Per-column matching rollup: "team (C): 5 matched, 2 new, 1 left out". */
export interface ColumnRollup {
  column: string;
  linked: number;
  created: number;
  ignored: number;
}

export function rollupColumns(items: ReconcileItem[], decisions: EntityDecisions): ColumnRollup[] {
  const byColumn = new Map<string, ColumnRollup>();
  const get = (column: string): ColumnRollup => {
    let r = byColumn.get(column);
    if (!r) {
      r = { column, linked: 0, created: 0, ignored: 0 };
      byColumn.set(column, r);
    }
    return r;
  };
  for (const item of items) {
    const action = decisionFor(decisions, item).action;
    for (const column of item.columns) {
      const r = get(column);
      if (action === 'link') r.linked += 1;
      else if (action === 'ignore') r.ignored += 1;
      else r.created += 1;
    }
  }
  return Array.from(byColumn.values());
}

/** The cells one column could not write, as one line: how many, and a few
 *  of the values as the sheet wrote them. */
export interface UnwrittenColumn {
  column: string;
  /** The datatype in the wizard's word: Integer, Date. */
  datatype: string;
  cells: number;
  examples: string[];
}

export function unwrittenByColumn(
  rejected: { column: string; value: string; datatype: string }[],
): UnwrittenColumn[] {
  const byColumn = new Map<string, UnwrittenColumn>();
  for (const r of rejected) {
    const key = `${r.column}\u0000${r.datatype}`;
    let c = byColumn.get(key);
    if (!c) {
      const word = r.datatype.replace(/^xsd:/, '');
      c = { column: r.column, datatype: word.charAt(0).toUpperCase() + word.slice(1), cells: 0, examples: [] };
      byColumn.set(key, c);
    }
    c.cells += 1;
    if (c.examples.length < 3 && !c.examples.includes(r.value)) c.examples.push(r.value);
  }
  return Array.from(byColumn.values());
}

/** A term in plain words: `:jersey_number` reads "jersey number",
 *  `:playsForTeam` "plays for team". For reading, never for writing. */
export function plainWords(term: string): string {
  const local = term.replace(/^.*[:#/]/, '');
  return local
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
}

/** A class as a reader names it: `:Player` reads "Player". */
export function className(term: string | undefined): string {
  return term ? term.replace(/^.*[:#/]/, '') : 'thing';
}
